import {Alert, Button, Col, DatePicker, Drawer, Form, Input, Row, TimePicker} from 'antd';
import {DrawerProps} from 'antd/lib/drawer';
import {FormComponentProps} from 'antd/lib/form';
import ContractSelect from 'components/form/ContractSelect';
import {LocationSelect} from 'components/form/select/LocationSelect';
import gql from 'graphql-tag';
import moment from 'moment';
import React, {useCallback} from 'react';
import {useMutation} from 'react-apollo';
import AircraftSelect from '../../form/AircraftSelect';
import PilotSelect from '../../form/PilotSelect';
import WithOrgData, {WithOrgDataProps} from '../../WithOrgData';
import {useGenerateFlightDesig} from "../../scheduling/util/use-generate-flight-desig";
import CstWarning from "../../form/CstWarning";

const formItemLayout = {
    labelCol: {
        xs: { span: 24 },
        sm: { span: 8 },
    },
    wrapperCol: {
        xs: { span: 24 },
        sm: { span: 16 },
    },
};
const tailFormItemLayout = {
    wrapperCol: {
        xs: {
        span: 24,
        offset: 0,
        },
        sm: {
        span: 16,
        offset: 8,
        },
    },
};

const CREATE_FLIGHT = gql`
mutation CreateFlight($payload: FlightNodeInput!, $orgID: ID!){
    setFlight(
        payload: $payload,
        orgID: $orgID
    ){
        _id
    }
}
`

const defaultFormOptions = (initialValue: any = '', required = true) => ({
    rules: [
        {
            required,
            message: 'This field is required'
        }
    ],
    initialValue
})

export interface NewFlightDrawerProps extends Partial<FormComponentProps>, WithOrgDataProps, DrawerProps {
    onCompleted?(data: any): void
}

const NewFlightDrawer: React.FC<NewFlightDrawerProps> = (props) => {

    const { onCompleted, orgData, form, ...restProps } = props;

    const generatedDesigResult = useGenerateFlightDesig({
        tpID: orgData.getOrgIDByType('transporter'),
        customerID: orgData.getOrgIDByType('customer'),
        contractName: form.getFieldValue('contractID')?.label,
        scheduledFlightDate: moment(form.getFieldValue('scheduledFlightDate')).format('YYYY-MM-DD')
    })
    
    const [ createFlight, { loading, error } ] = useMutation(CREATE_FLIGHT, {
        onCompleted: (data) => props.onCompleted && props.onCompleted(data && data.setFlight)
    })

    function submit(e: React.FormEvent<HTMLFormElement>){
        e.preventDefault();
        form.validateFieldsAndScroll((err, values) => {
            const payload = {
                ...values,
                contractID: values.contractID?.key,
                customerID: orgData.getOrgIDByType('customer'),
                tpID: orgData.getOrgIDByType('transporter'),
                lastKnownController: values.lastKnownController,
                departureID: values.departureID.key,
                scheduledFlightDate: moment(values.scheduledFlightDate).format('YYYY-MM-DD'),
                desig: values.desig || generatedDesigResult.desig,
                estDepartureTime: values.estDepartureTime?.format('HH:mm:00'),
            }

            if (!err) {
                createFlight({
                    variables: {
                        payload: payload,
                        orgID: orgData.getActiveOrgID()
                    }
                })
            }
        });
    }

    const validateDesig = useCallback((rule, value, callback) => {
        if (!value && !generatedDesigResult.desig && rule.required){
            callback(rule.message);
            return;
        }
        callback();
    }, [form, generatedDesigResult.desig])

    function renderContent(){
        const { getFieldDecorator, getFieldValue } = form;

        return (
            <Form onSubmit={submit}>
            {error && <Alert type="error" message="Failed to create flight" description={error.message} showIcon />}
                <Form.Item {...formItemLayout} label="Scheduled Flight Date">
                    {getFieldDecorator('scheduledFlightDate', defaultFormOptions(undefined))(
                        <DatePicker
                            disabledDate={current => current < moment().startOf('day')}
                            format="YYYY-MM-DD"
                        />
                    )}
                </Form.Item>
                <Form.Item {...formItemLayout} label="Est. Departure Time">
                    {getFieldDecorator('estDepartureTime', defaultFormOptions(undefined))(
                        <TimePicker format="HH:mm" />
                    )}
                    <CstWarning style={{ marginLeft: 6 }} />
                </Form.Item>
                <Form.Item {...formItemLayout} label="Flight Designation">
                    {getFieldDecorator('desig', {
                        initialValue: '',
                        rules: [
                            {
                                message: 'Please enter a flight desig or assign a contract.',
                                required: true,
                                validator: validateDesig
                            }
                        ]
                    })(
                        <Input
                            placeholder={generatedDesigResult.desig}
                        />
                    )}
                </Form.Item>
                <Form.Item {...formItemLayout} label="Controlling Heliport">
                    {getFieldDecorator('lastKnownController', defaultFormOptions())(
                        <LocationSelect
                            type='ONSHORE'
                            includeOneOff
                            hasDataflytHardware
                            limit={50}
                        />
                    )}
                </Form.Item>
                <Form.Item {...formItemLayout} label="Departure">
                    {getFieldDecorator('departureID', defaultFormOptions())(
                        <LocationSelect labelInValue />
                    )}
                </Form.Item>
                <Form.Item {...formItemLayout} label="Aircraft">
                    {getFieldDecorator('aircraftID', defaultFormOptions())(
                        <AircraftSelect />
                    )}
                </Form.Item>
                <Form.Item {...formItemLayout} label="Contract">
                    {getFieldDecorator('contractID', defaultFormOptions())(
                        <ContractSelect
                            orgType="customer"
                            placeholder="Choose a contract"
                            labelInValue
                        />
                    )}
                </Form.Item>
                <Form.Item {...formItemLayout} label="Pilot">
                    {getFieldDecorator('pilotID', defaultFormOptions('', false))(
                        <PilotSelect />
                    )}
                </Form.Item>
                <Form.Item {...formItemLayout} label="Co-Pilot">
                    {getFieldDecorator('copilotID', defaultFormOptions('', false))(
                        <PilotSelect />
                    )}
                </Form.Item>
                <Form.Item {...tailFormItemLayout}>
                    <Row type="flex" gutter={6}>
                        <Col>
                            <Button htmlType="submit" loading={loading} type="primary">Create Flight</Button>
                        </Col>
                        <Col>
                            <Button onClick={() => form.resetFields()}>Reset</Button>
                        </Col>
                    </Row>
                </Form.Item>
            </Form>
        )
    }
    return <Drawer
        title="Create New Flight"
        closable
        width="35rem"
        {...restProps}
    >
        {renderContent()}
    </Drawer>
}

export default Form.create<NewFlightDrawerProps & FormComponentProps>({
    onValuesChange:(props, changedValues, allValues) => {
        if(changedValues["lastKnownController"]){
            props.form.setFieldsValue({"contractID": ""})
        }
    }})(WithOrgData(NewFlightDrawer))